import { Direction, Leg, Plan, Step, StopWithTime } from 'src/generated/brezavta/requests/types.gen'
import { isSignificantDirection } from 'src/lib/directions'

export const TRANSIT_MODES = ['BUS', 'RAIL', 'TRAM', 'SUBWAY', 'FERRY', 'CABLE_CAR', 'GONDOLA', 'FUNICULAR']

function haversine(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371000
    const φ1 = (lat1 * Math.PI) / 180, φ2 = (lat2 * Math.PI) / 180
    const Δφ = ((lat2 - lat1) * Math.PI) / 180, Δλ = ((lon2 - lon1) * Math.PI) / 180
    const a = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2
    return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

export function minDistToLeg(leg: Leg, lat: number, lon: number): number {
    if (leg.geometry?.coordinates?.length) {
        let min = Infinity
        for (const [pLon, pLat] of leg.geometry.coordinates) {
            const d = haversine(lat, lon, pLat, pLon)
            if (d < min) min = d
        }
        return min
    }
    return Math.min(
        haversine(lat, lon, leg.from_point.lat, leg.from_point.lon),
        haversine(lat, lon, leg.to_point.lat, leg.to_point.lon),
    )
}

export function detectCurrentLeg(plan: Plan, lat: number, lon: number): number {
    const now = Date.now()
    let timeIdx = 0
    for (let i = 0; i < plan.legs.length; i++) {
        if (now >= plan.legs[i].start_time) timeIdx = i
    }
    const distToTime = minDistToLeg(plan.legs[timeIdx], lat, lon)
    if (distToTime > 400) return timeIdx

    let best = timeIdx
    let bestDist = distToTime
    for (const i of [timeIdx - 1, timeIdx, timeIdx + 1]) {
        if (i < 0 || i >= plan.legs.length) continue
        const d = minDistToLeg(plan.legs[i], lat, lon)
        if (d < bestDist) {
            bestDist = d
            best = i
        }
    }
    return best
}

export function closestGeomIdx(coords: number[][], lat: number, lon: number): number {
    let best = 0, bestDist = Infinity
    coords.forEach(([pLon, pLat], i) => {
        const d = haversine(lat, lon, pLat, pLon)
        if (d < bestDist) {
            bestDist = d
            best = i
        }
    })
    return best
}

export function buildStopGeomIndices(leg: Leg): Map<string, number> {
    const m = new Map<string, number>()
    if (!leg.geometry?.coordinates?.length || !leg.intermediate_stops?.length) return m
    const coords = leg.geometry.coordinates
    for (const stop of leg.intermediate_stops) {
        m.set(stop.gtfs_id, closestGeomIdx(coords, stop.lat, stop.lon))
    }
    return m
}

export function findNextStop(
    leg: Leg,
    userLat: number | null,
    userLon: number | null,
    stopIndices: Map<string, number>,
): StopWithTime | null {
    if (!leg.intermediate_stops?.length) return null
    const coords = leg.geometry?.coordinates
    if (userLat != null && userLon != null && coords?.length && stopIndices.size) {
        const userIdx = closestGeomIdx(coords, userLat, userLon)
        const ahead = leg.intermediate_stops
            .map(s => ({ s, idx: stopIndices.get(s.gtfs_id) ?? 0 }))
            .filter(x => x.idx > userIdx)
            .sort((a, b) => a.idx - b.idx)
        return ahead[0]?.s ?? null
    }
    return leg.intermediate_stops.find(s => (s.departure_time ?? 0) > Date.now()) ?? null
}

export function buildStepGeomIndices(leg: Leg): Map<number, number> {
    const m = new Map<number, number>()
    if (!leg.geometry?.coordinates?.length || !leg.steps?.length) return m
    const coords = leg.geometry.coordinates
    leg.steps.forEach((step, i) => {
        m.set(i, closestGeomIdx(coords, step.lat, step.lon))
    })
    return m
}

export function findNextWalkStep(
    leg: Leg,
    userLat: number,
    userLon: number,
    stepIndices: Map<number, number>,
): Step | null {
    const steps = leg.steps
    if (!steps?.length) return null
    const coords = leg.geometry?.coordinates

    if (!coords?.length || !stepIndices.size) {
        return steps.find(s => isSignificantDirection(s.relative_direction as Direction)) ?? null
    }

    const userIdx = closestGeomIdx(coords, userLat, userLon)

    // Find the last step whose start is at or before the user (= current step)
    let currentStepI = 0
    for (let i = 0; i < steps.length; i++) {
        const sIdx = stepIndices.get(i) ?? 0
        if (sIdx <= userIdx) currentStepI = i
        else break
    }

    // Return first significant step after current
    for (let i = currentStepI + 1; i < steps.length; i++) {
        if (isSignificantDirection(steps[i].relative_direction as Direction)) return steps[i]
    }
    return null
}

export interface SimPt { lat: number, lon: number, legIdx: number }

export function buildSimPoints(plan: Plan): SimPt[] {
    const pts: SimPt[] = []
    plan.legs.forEach((leg, legIdx) => {
        if (leg.geometry?.coordinates?.length) {
            for (const [lon, lat] of leg.geometry.coordinates) pts.push({ lat, lon, legIdx })
        }
        else {
            pts.push({ lat: leg.from_point.lat, lon: leg.from_point.lon, legIdx })
            pts.push({ lat: leg.to_point.lat, lon: leg.to_point.lon, legIdx })
        }
    })
    return pts
}

export function fitCurrentLegs(map: maplibregl.Map, plan: Plan, idx: number) {
    const legs = plan.legs.slice(idx, idx + 2).filter((l: Leg) => l.geometry?.coordinates?.length)
    if (!legs.length) return
    const b: [[number, number], [number, number]] = [[180, 90], [-180, -90]]
    for (const leg of legs) {
        for (const [lon, lat] of leg.geometry!.coordinates) {
            b[0][0] = Math.min(b[0][0], lon)
            b[0][1] = Math.min(b[0][1], lat)
            b[1][0] = Math.max(b[1][0], lon)
            b[1][1] = Math.max(b[1][1], lat)
        }
    }
    map.fitBounds(b, { padding: { top: 80, bottom: 260, left: 50, right: 50 }, duration: 800 })
}
